// Pure rules for changing a user's single investor from the admin edit modal.
// Mirrors the backend: blocking cases are also enforced there (409); this only
// decides what to warn about / disable before the request is sent.
import type { InvestorMemberRole, UserInvestorMembership } from '../types/investor';

/** What the admin picked in the section: an investor + role, or none. */
export interface MembershipSelection {
  investorId: string; // '' = no investor
  role: InvestorMemberRole | '';
}

export type MembershipChangeKind = 'none' | 'assign' | 'move' | 'role' | 'remove';

export type MembershipWarning =
  | 'loseWebAccess' // USER → INVESTOR_HUB
  | 'loseCurrentInvestor' // moving away from the current investor
  | 'userWillBeDeleted'; // removing a Hub-only user's investor deletes them

export interface MembershipChange {
  kind: MembershipChangeKind;
  warnings: MembershipWarning[];
  /** Set when the change would leave the current investor without a SUPERADMIN. */
  blockedByLastSuperadmin: boolean;
}

/** Classifies the change and lists the warnings/blocks that apply to it. */
export function describeMembershipChange(
  context: UserInvestorMembership,
  selection: MembershipSelection
): MembershipChange {
  const current = context.membership;
  const kind = classify(current, selection);
  const warnings: MembershipWarning[] = [];

  if ((kind === 'assign' || kind === 'move') && context.userRole === 'USER') {
    warnings.push('loseWebAccess');
  }
  if (kind === 'move') warnings.push('loseCurrentInvestor');
  if (kind === 'remove' && context.userRole === 'INVESTOR_HUB') {
    warnings.push('userWillBeDeleted');
  }

  // Leaving the SUPERADMIN seat: removed, moved away, or demoted in place.
  const leavesSuperadminSeat =
    current?.role === 'SUPERADMIN' &&
    (kind === 'remove' || kind === 'move' || (kind === 'role' && selection.role !== 'SUPERADMIN'));

  return {
    kind,
    warnings,
    blockedByLastSuperadmin: leavesSuperadminSeat && context.isLastSuperadmin,
  };
}

function classify(
  current: UserInvestorMembership['membership'],
  selection: MembershipSelection
): MembershipChangeKind {
  if (!selection.investorId) return current ? 'remove' : 'none';
  if (!current) return 'assign';
  if (current.investorId !== selection.investorId) return 'move';
  return current.role === selection.role ? 'none' : 'role';
}
